<template>
  <!-- ── แถบเตือนใกล้หมดอายุ ─────────────────────────────────────────────────
       ขึ้นเฉพาะตอน "ไม่ได้ใช้งาน" — ถ้าเพิ่งขยับ session.js ต่ออายุให้เงียบ ๆ ไปแล้ว
       ผู้ใช้จะไม่เคยเห็นแถบนี้เลย ซึ่งคือพฤติกรรมที่ต้องการ (§6) -->
  <v-snackbar v-model="warning" :timeout="-1" location="top" color="warning" class="sg-bar">
    <v-icon start>mdi-clock-alert-outline</v-icon>
    {{ $t('session.expiringIn', { n: countdown }) }}
    <template #actions>
      <v-btn variant="text" :loading="renewing" @click="stayLoggedIn">
        {{ $t('session.stayLoggedIn') }}
      </v-btn>
    </template>
  </v-snackbar>

  <!-- ── ล็อกอินซ้อนทับ ───────────────────────────────────────────────────────
       🔑 **ซ้อนทับ ไม่ใช่เด้งออก** (เจ้าของเคาะ) — หน้าจอเดิมยังอยู่ใต้ dialog นี้ทั้งดุ้น
       ทั้ง state ของฟอร์มและ dialog ที่เปิดค้างอยู่ · เดิมพอโทเคนหมดจะเด้งกลับหน้าล็อกอิน
       แล้วของที่กรอกมาทั้งหน้าหายหมด ซึ่งคือเหตุผลหลักที่ทำเรื่องนี้ -->
  <v-dialog v-model="expired" persistent max-width="420" :scrim="true">
    <v-card class="pa-4 sg-card">
      <div class="text-center mb-3">
        <v-icon size="42" color="warning">mdi-lock-clock</v-icon>
        <h3 class="mt-2">{{ $t('session.endedTitle') }}</h3>
        <p class="text-medium-emphasis mt-1 sg-sub">{{ endedText }}</p>
      </div>

      <v-form @submit.prevent="relogin">
        <!-- ชื่อผู้ใช้อ่านอย่างเดียว: นี่คือการ "ยืนยันตัวอีกครั้ง" ของคนเดิม ไม่ใช่การสลับบัญชี
             (ถ้าให้แก้ได้ คนใหม่จะล็อกอินทับแล้วเห็นฟอร์มที่คนเก่ากรอกค้างไว้ใต้ dialog) -->
        <!-- ต้องมี `autocomplete="username"` คู่กับ current-password เสมอ — ไม่งั้นเบราว์เซอร์จะ
             ยัดชื่อผู้ใช้ลงช่อง text ที่ใกล้ที่สุด ซึ่งที่นี่คือช่องค้นหาของกริดที่ **ตั้งใจให้ยัง
             อยู่ใต้ overlay** แล้วกริดจะยิงค้นหาเองเงียบ ๆ (เจ้าของเคยเจอบั๊กนี้มาแล้ว) -->
        <v-text-field :model-value="username" :label="$t('auth.username')" readonly
          autocomplete="username" name="sg-username"
          variant="outlined" density="compact" prepend-inner-icon="mdi-account" class="mb-2" />
        <v-text-field v-model="password" :label="$t('auth.password')" type="password"
          variant="outlined" density="compact" prepend-inner-icon="mdi-lock"
          autocomplete="current-password" autofocus :error-messages="error" @input="error = ''" />
        <!-- หมายเหตุ: กด Enter แล้วไม่ส่งฟอร์ม — เป็นพฤติกรรมเดิมของแอป (หน้า Login.vue หลัก
             ก็เป็นเหมือนกัน ทดสอบแล้ว) จึงไม่ใส่ตัวดักเพิ่มให้ต่างจากที่ผู้ใช้คุ้นอยู่ -->
        <v-btn type="submit" color="primary" block :loading="busy" class="mt-2">
          {{ $t('session.continue') }}
        </v-btn>
        <v-btn variant="text" color="grey-darken-1" block class="mt-1" @click="giveUp">
          {{ $t('session.logoutInstead') }}
        </v-btn>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
// SessionGuard — หน้าตาของนโยบายเซสชัน §6 ทั้งหมดอยู่ในคอมโพเนนต์เดียว
// ตรรกะ (นับ activity / ต่ออายุ / ล็อกหลายแท็บ) อยู่ที่ `utils/session.js` · สายอยู่ที่ `utils/live.js`
import axios from 'axios';
import CryptoJS from 'crypto-js';
import live from '@/utils/live';
import session from '@/utils/session';

export default {
  name: 'SessionGuard',
  emits: ['onLoggedOut', 'onRelogin'],
  data() {
    return {
      warning: false, countdown: 60, renewing: false,
      expired: false, endedReason: '', password: '', error: '', busy: false,
      ticker: null, lastLoginAt: 0, expiredUser: '',
    };
  },
  computed: {
    username() {
      // อ่านจาก store ก่อน — ถ้าแท็บอื่นกด logout `localStorage.userdata` จะถูกลบไปแล้ว
      // แล้วช่องนี้จะว่างและแก้ไม่ได้ (readonly) = ผู้ใช้ติดอยู่กับ dialog ที่ทำอะไรไม่ได้เลย
      const fromStore = this.$store.getters.getUserdata;
      if (fromStore && fromStore.username) return fromStore.username;
      try { return (JSON.parse(localStorage.getItem('userdata') || '{}') || {}).username || ''; }
      catch (e) { return ''; }
    },
    endedText() {
      if (this.endedReason === 'logout') return this.$t('session.endedLogout');
      if (this.endedReason === 'permission-changed') return this.$t('session.endedPermission');
      return this.$t('session.endedExpired');
    },
  },
  mounted() {
    session.start(this.$store, {
      onExpiring: (secs) => this.showWarning(secs),
      onEnded: (reason) => this.showExpired(reason),
      onRenewed: (d) => {
        this.hideWarning();
        // แท็บอื่นเพิ่งล็อกอินใหม่ให้ — โทเคนใหม่เข้าสโตร์แล้ว dialog ของแท็บนี้ต้องหายเอง
        // ไม่งั้นผู้ใช้เห็น dialog ค้าง แล้วกด "ออกจากระบบ" เพิกถอน session ใหม่ของอีกแท็บ
        if (d && d.fromOtherTab && this.expired) this.adoptOtherTabLogin();
      },
    });
  },
  unmounted() {
    session.stop();
    this.clearTicker();
  },
  methods: {
    clearTicker() { if (this.ticker) { clearInterval(this.ticker); this.ticker = null; } },

    showWarning(secs) {
      this.countdown = Math.max(1, secs || 60);
      this.warning = true;
      this.clearTicker();
      this.ticker = setInterval(() => {
        this.countdown -= 1;
        // ขยับระหว่างนับถอยหลัง = ต่อให้ทันที ไม่ต้องรอให้กดปุ่ม (§6)
        if (session.isActive()) { this.stayLoggedIn(); return; }
        if (this.countdown <= 0) { this.clearTicker(); this.warning = false; }
      }, 1000);
    },
    hideWarning() { this.warning = false; this.clearTicker(); },

    async stayLoggedIn() {
      if (this.renewing) return;
      this.renewing = true;
      session.markActivity();
      let ok = await session.renew(this.$store, { force: true });
      // 'retry' = ยิงไม่ถึง/เซิร์ฟเวอร์สะดุด ไม่ใช่ถูกปฏิเสธ — เน็ตยิมสะดุด 2 วิ ต้องไม่กลายเป็น
      // การบังคับล็อกอินใหม่ทั้งที่โทเคนยังดีอยู่ (และ showExpired จะปิดสายถาวรด้วย)
      if (ok === 'retry') {
        await new Promise((r) => setTimeout(r, 2000));
        ok = await session.renew(this.$store, { force: true });
      }
      this.renewing = false;
      if (ok === true || ok === 'retry') this.hideWarning();   // ลองแล้วยังไม่ถึง = ปล่อยให้ตัวจับเวลาลองต่อ
      else this.showExpired('expired');
    },

    showExpired(reason) {
      // 🔴 **กันเรียกซ้ำ** — ทุกจอในระบบยิง 401 เข้าทางนี้ และโพลเงียบ 60 วิก็ยิงซ้ำเรื่อย ๆ
      // ถ้าไม่กัน ช่องรหัสผ่านที่ผู้ใช้กำลังพิมพ์จะถูกล้างทุกนาที (เจอจริงในรีวิว)
      if (this.expired) return;
      // 🔴 **เมิน 401 ที่ค้างมาจากก่อนล็อกอินล่าสุด** — คำขอที่ออกไปด้วยโทเคนเก่าเมื่อ 2 วิก่อน
      // เพิ่ง resolve หลังล็อกอินสำเร็จ ถ้าไม่กัน dialog จะเด้งกลับมาทั้งที่เพิ่งล็อกอินเสร็จ
      // และ `live.stop()` จะไปปิดสายที่ relogin() เพิ่งเปิด
      if (Date.now() - this.lastLoginAt < 10000) return;
      this.hideWarning();
      this.endedReason = reason;
      this.expiredUser = this.username;
      this.password = '';
      this.error = '';
      this.expired = true;
      live.stop();   // ห้ามวนต่อสายด้วยโทเคนที่ตายแล้ว
    },

    async relogin() {
      if (!this.password) { this.error = this.$t('validation.passwordRequired'); return; }
      this.busy = true;
      try {
        const res = await axios.post(this.baseURL + '/login', {
          username: this.username,
          // ต้องเข้ารหัสแบบเดียวกับ Login.vue เป๊ะ ไม่งั้นรหัสถูกก็ล็อกอินไม่ผ่าน
          password: CryptoJS.SHA256(this.password).toString(),
        });
        if (!res.data || !res.data.success || !res.data.token) {
          this.error = (res.data && res.data.message) || this.$t('session.loginFailed');
          return;
        }
        // userdata ก่อน token: แท็บอื่นรับ event ของ token แล้วอ่าน userdata ทันที
        if (res.data.userdata) localStorage.setItem('userdata', JSON.stringify(res.data.userdata));
        localStorage.setItem('token', res.data.token);
        this.$store.dispatch('setToken', { token: res.data.token, userdata: localStorage.getItem('userdata') });
        this.expired = false;
        this.password = '';
        this.lastLoginAt = Date.now();
        session.noteNewToken(res.data.token);
        // ล็อกอินใหม่ = session ใหม่ (sid ใหม่) สายเดิมใช้ไม่ได้แล้ว ต้องเริ่มใหม่ทั้งดุ้น
        live.start(() => this.$store.getters.getToken);
        session.markActivity();
        // เหตุผลหนึ่งของ overlay คือ "สิทธิ์เปลี่ยน" — ถ้าไม่โหลดเมนูใหม่ ผู้ใช้จะเห็นเมนูชุดเก่า
        // แล้วกดเข้าไปโดน 403 ซึ่งวนกลับมาที่ dialog นี้อีกรอบ
        this.$emit('onRelogin');
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || this.$t('session.loginFailed');
      } finally {
        this.busy = false;
      }
    },

    // ทำสิ่งเดียวกับท้าย relogin() แต่โทเคนมาจากแท็บอื่น (เก็บลง localStorage/store ให้แล้ว)
    adoptOtherTabLogin() {
      // คนละบัญชี (เครื่องใช้ร่วมกัน: แท็บอื่นออกแล้วอีกคนล็อกอิน) — จอนี้ยังเป็นข้อมูลของคนเดิม
      // ห้ามสวมโทเคนคนใหม่ทับเงียบ ๆ โหลดแอปใหม่ให้เริ่มจากตัวตนใหม่ทั้งดุ้น
      if (this.expiredUser && this.username && this.username !== this.expiredUser) {
        window.location.reload();
        return;
      }
      this.expired = false;
      this.password = '';
      this.error = '';
      this.lastLoginAt = Date.now();   // กัน 401 ค้างจากโทเคนเก่าเด้ง dialog กลับมา
      session.markActivity();
      live.start(() => this.$store.getters.getToken);
      this.$emit('onRelogin');
    },

    giveUp() {
      this.expired = false;
      this.$emit('onLoggedOut');
    },
  },
};
</script>

<style scoped>
.sg-sub { font-size: .9rem; }
.sg-card { border-radius: 14px; }
</style>
